import Box from '@app/components/common/Box';
import Grid from '@app/components/common/Grid';
import Typography from '@app/components/common/Typography';
import { formatValue as _formatValue } from '@app/helpers/modals/diffModal';
import useItemChangeColor from '@app/hooks/useItemChangeColor';
import useRootSelector from '@app/hooks/useRootSelector';
import {
  selectChangeDataId,
  selectFinalDiffObject,
} from '@app/state/menu/menuSelectors';
import {
  IAvailabilityType,
  ISpecial,
  ISpecialDiffObject,
  SPECIAL_INITIAL_STATE,
  TChangeItems,
  TSpecialMenuTypeVersionDiff,
  formatMoney,
} from '@westondev/tableturn-core';
import some from 'lodash/some';
import { Fragment, useMemo, Dispatch, SetStateAction } from 'react';
import { WithTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import RenderChange from '../../SettingsDiffConfirmationModal/common/RenderChange';
import HeaderDiffModal from '../../common/HeaderDiffModal';
import MenuTypeVersionsButtons from '../../common/MenuTypeVersionsButtons';
import TextAreaChange from '../../common/TextAreaChange';
import AvailabilityTableDiff from './AvailabilityTableDiff';
import { specialsDiffModalStyles } from './styles';

interface SpecialsDiffModalProps extends WithTranslation {
    selectedMenuTypeVersion: number;
    setSelectedMenuTypeVersion: Dispatch<SetStateAction<number>>;
}

const SpecialsDiffModal = ({
    t,
    selectedMenuTypeVersion,
    setSelectedMenuTypeVersion,
}: SpecialsDiffModalProps) => {
  // Redux
  const finalDiffObject = useSelector(selectFinalDiffObject);
  const specialId = useSelector(selectChangeDataId, shallowEqual);
  const originalData = useRootSelector(
    state =>
      state.menu.selectedMode.currentMenu.specials[specialId] ||
      SPECIAL_INITIAL_STATE,
  ) as ISpecial;

  const formattedDifferences = finalDiffObject as ISpecialDiffObject;

  // local state
  const { borderContainerColor } = useItemChangeColor();

  const selectedMenuTypeVersionId = useMemo(
    () =>
      formattedDifferences.menuTypeVersions[selectedMenuTypeVersion]?.id || -1,
    [selectedMenuTypeVersion, formattedDifferences],
  );

  const renderMenuTypeVersionsButtons = (
    menuTypeVersions: TSpecialMenuTypeVersionDiff[],
  ) => {
    return (
      <MenuTypeVersionsButtons
        menuTypeVersions={menuTypeVersions}
        selectedMenuTypeVersion={selectedMenuTypeVersion}
        onPress={setSelectedMenuTypeVersion}
      />
    );
  };

  const renderAvailabilityTable = (times: IAvailabilityType) => {
    return (
      <AvailabilityTableDiff
        availabilityRules={times}
        formattedDifferences={
          formattedDifferences.menuTypeVersions[selectedMenuTypeVersion]
            .sections.availability
        }
        originalData={originalData.menuTypeVersions[selectedMenuTypeVersionId]}
      />
    );
  };

  const formatValue = (value: any, field: string, textValue?: string) => {
    if (['specialApplyTo', 'discountType', 'discountAmount'].includes(field)) {
      return textValue;
    } else if (typeof value === 'number') {
      // if value is number, format the number depending on the field
      if (['thresholdAmount'].includes(field)) {
        return formatMoney(null, value);
      } else return value.toString();
    } else if (typeof value === 'string') {
      if (field === 'customizerShape') {
        return textValue;
      } else return value;
    } else return _formatValue(value, field);
  };

  const renderChange = (change: TChangeItems) => {
    return <RenderChange formatValue={formatValue} change={change} textCsx={{ fontSize: '14px' }} />;
  };

  return (
    <>
      {formattedDifferences && (
        <>
          {formattedDifferences.masterChanges.length > 0 && (
            <>
              <HeaderDiffModal
                name={t('app.modals.diffModal.sectionTitles.commonChanges')}
                color="black"
                containerCsx={{ marginTop: '20px' }}
              />
              <Grid columnGap={{ mb: 20, md: 60 }} rowGap={10}>
                {formattedDifferences.masterChanges
                  .filter(change => change.field !== 'locationIds')
                  .map(change => (
                    <Grid.Item
                      mb={12}
                      sm={6}
                      md={4}
                      lg={4}
                      key={`master_change_${change.field}`}>
                      {renderChange(change)}
                    </Grid.Item>
                  ))}
                <Grid.Item>
                  {formattedDifferences.masterChanges
                    .filter(change => change.field === 'locationIds')
                    .map(change => (
                      <Fragment key={`master_change_location_${change.field}}`}>
                        {renderChange(change)}
                      </Fragment>
                    ))}
                </Grid.Item>
              </Grid>
            </>
          )}
        </>
      )}
      {formattedDifferences &&
        formattedDifferences.menuTypeVersions.length > 0 && (
          <>
            {renderMenuTypeVersionsButtons(
              formattedDifferences.menuTypeVersions,
            )}
            <Box
              csx={[
                specialsDiffModalStyles,
                {
                  backgroundColor: 'white',
                  border: `1px solid ${borderContainerColor}`,
                  marginBottom: '5px',
                },
              ]}>
              {formattedDifferences.menuTypeVersions[selectedMenuTypeVersion]
                .action === 'remove' && (
                <Typography align="center" color="persistentSemanticRed">
                  {t('app.modals.itemsDiff.menuType.menuTypeRemoved')}
                </Typography>
              )}
              {formattedDifferences.menuTypeVersions[selectedMenuTypeVersion]
                .sections.basic.length > 0 && (
                <>
                  <HeaderDiffModal
                    name={t('app.modals.diffModal.sectionTitles.basic')}
                  />
                  <Grid columnGap={{ mb: 20, md: 60 }} rowGap={10}>
                    {formattedDifferences.menuTypeVersions[
                      selectedMenuTypeVersion
                    ].sections.basic.map(change => (
                      <Grid.Item
                        mb={12}
                        sm={6}
                        md={4}
                        lg={4}
                        key={`basic_change_${change.field}`}>
                        {renderChange(change)}
                      </Grid.Item>
                    ))}
                  </Grid>
                </>
              )}
              {formattedDifferences.menuTypeVersions[selectedMenuTypeVersion]
                .sections.moreInfo.length > 0 && (
                <>
                  <HeaderDiffModal
                    name={t(
                      'app.modals.diffModal.sectionTitles.moreInformation',
                    )}
                  />
                  {formattedDifferences.menuTypeVersions[
                    selectedMenuTypeVersion
                  ].sections.moreInfo.map((change, index) => (
                    <Fragment key={`change-${index}`}>
                      {change.value ? (
                        <TextAreaChange change={change} />
                      ) : (
                        renderChange(change)
                      )}
                    </Fragment>
                  ))}
                </>
              )}
              {some(
                formattedDifferences.menuTypeVersions[selectedMenuTypeVersion]
                  .sections.availability,
                days => days.length > 0,
              ) && (
                <Box>
                  <HeaderDiffModal
                    name={t(
                      'app.modals.specialsDiff.sectionTitles.availability',
                    )}
                  />
                  {renderAvailabilityTable(
                    formattedDifferences.menuTypeVersions[
                      selectedMenuTypeVersion
                    ].sections.availability,
                  )}
                </Box>
              )}
              {formattedDifferences.menuTypeVersions[selectedMenuTypeVersion]
                .sections.specialsScheme.length > 0 && (
                <>
                  <HeaderDiffModal
                    name={t(
                      'app.modals.specialsDiff.sectionTitles.specialsScheme',
                    )}
                  />
                  <Grid columnGap={{ mb: 20, md: 60 }} rowGap={10}>
                    {formattedDifferences.menuTypeVersions[
                      selectedMenuTypeVersion
                    ].sections.specialsScheme.map(change => (
                      <Grid.Item
                        mb={12}
                        sm={6}
                        md={4}
                        lg={4}
                        key={`specialsScheme_change_${change.field}`}>
                        {renderChange(change)}
                      </Grid.Item>
                    ))}
                  </Grid>
                </>
              )}
              {(formattedDifferences.menuTypeVersions[selectedMenuTypeVersion]
                .sections.associations.buyItems.length > 0 ||
                formattedDifferences.menuTypeVersions[selectedMenuTypeVersion]
                  .sections.associations.getItems.length > 0 ||
                formattedDifferences.menuTypeVersions[selectedMenuTypeVersion]
                  .sections.associations.buyCombos.length > 0 ||
                formattedDifferences.menuTypeVersions[selectedMenuTypeVersion]
                  .sections.associations.getCombos.length > 0 ||
                formattedDifferences.menuTypeVersions[selectedMenuTypeVersion]
                  .sections.associations.getItemsChanges.length > 0) && (
                <>
                  <HeaderDiffModal
                    name={t('app.modals.diffModal.sectionTitles.associations')}
                  />
                  {formattedDifferences.menuTypeVersions[
                    selectedMenuTypeVersion
                  ].sections.associations.buyItems.length > 0 &&
                    renderChange({
                      field: 'buyItemIds',
                      label:
                        formattedDifferences.menuTypeVersions[
                          selectedMenuTypeVersion
                        ].sections.associations.buyItems[0].label,
                      value:
                        formattedDifferences.menuTypeVersions[
                          selectedMenuTypeVersion
                        ].sections.associations.buyItems,
                    })}
                  {formattedDifferences.menuTypeVersions[
                    selectedMenuTypeVersion
                  ].sections.associations.buyCombos.length > 0 &&
                    renderChange({
                      field: 'buyComboIds',
                      label:
                        formattedDifferences.menuTypeVersions[
                          selectedMenuTypeVersion
                        ].sections.associations.buyCombos[0].label,
                      value:
                        formattedDifferences.menuTypeVersions[
                          selectedMenuTypeVersion
                        ].sections.associations.buyCombos,
                    })}

                  {formattedDifferences.menuTypeVersions[
                    selectedMenuTypeVersion
                  ].sections.associations.getItemsChanges.length > 0 && (
                    <Grid columnGap={{ mb: 20, md: 60 }} rowGap={10}>
                      {formattedDifferences.menuTypeVersions[
                        selectedMenuTypeVersion
                      ].sections.associations.getItemsChanges.map(change => (
                        <Grid.Item
                          mb={12}
                          sm={6}
                          md={4}
                          lg={4}
                          key={`getItemsChanges_${change.field}`}>
                          {renderChange(change)}
                        </Grid.Item>
                      ))}
                    </Grid>
                  )}
                  {formattedDifferences.menuTypeVersions[
                    selectedMenuTypeVersion
                  ].sections.associations.getItems.length > 0 &&
                    renderChange({
                      field: 'getItemIds',
                      label: t(
                        'app.modals.specialsDiff.getItemsFields.getItemIds.title.bogo.items',
                      ),
                      value:
                        formattedDifferences.menuTypeVersions[
                          selectedMenuTypeVersion
                        ].sections.associations.getItems,
                    })}

                  {formattedDifferences.menuTypeVersions[
                    selectedMenuTypeVersion
                  ].sections.associations.getCombos.length > 0 &&
                    renderChange({
                      field: 'getCombos',
                      label: t(
                        'app.modals.specialsDiff.getItemsFields.getItemIds.title.bogo.combos',
                      ),
                      value:
                        formattedDifferences.menuTypeVersions[
                          selectedMenuTypeVersion
                        ].sections.associations.getCombos,
                    })}
                </>
              )}
            </Box>
          </>
        )}
    </>
  );
};

export default SpecialsDiffModal;
